import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { findDocs } from 'shared/utils/pouchDB/general';
import { useDB } from 'shared/Providers/UserDB';
import { TDocProject } from 'shared/model/project';
import { PrimitiveAtom } from 'jotai/vanilla/atom';
import { TDocument } from 'shared/model/doc-base';
import { ListAtomValue, ListSettingsAtomValue } from 'shared/_components/list/types';
import { getListOptions } from 'shared/_components/list/utils';
import { DocType } from 'shared/constants/docs';

type UseDocumentListProps<T extends TDocument> = {
	type: DocType;
	listAtom: PrimitiveAtom<ListAtomValue<T>> & { init: ListAtomValue<T> };
	settingsAtom: PrimitiveAtom<ListSettingsAtomValue> & { init: ListSettingsAtomValue };
	onRefresh?: (value: ListAtomValue<TDocProject>, db: PouchDB.Database<TDocument>) => Promise<void>;
	doSetList?: boolean;
}

export function useDocumentList<T extends TDocument>(props: UseDocumentListProps<T>) {
	const { type, listAtom, settingsAtom, onRefresh, doSetList = true } = props;
	const {
		// page, // TODO: do pagination
		filters,
		sortBy,
	} = useAtomValue(settingsAtom);

	const options = useMemo(() => getListOptions({
		type,
		filters,
		sortBy,
	}), [filters, sortBy, type]);

	const { db } = useDB();
	const setList = useSetAtom(listAtom);
	/** True once the list was read for the first time (an empty list is then really empty, not still loading). */
	const [loaded, setLoaded] = useState(false);

	const refresh = useCallback(async () => {
		if (!db) {
			return;
		}
		const items = await findDocs(options, db);
		const result = { items, total: items.length };
		await onRefresh?.(result, db);
		if (doSetList) {
			setList({ items, total: items.length });
		}
		setLoaded(true);
	}, [db, options, onRefresh, doSetList, setList]);

	useEffect(() => {
		refresh();
	}, [refresh]);

	return useMemo(() => ({ refresh, loaded }), [refresh, loaded]);
}
